import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

function MediaTile({ item, fetchDownloadUrl, onSelect, selectedId }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loadingThumb, setLoadingThumb] = useState(true);
  const isSelected = selectedId != null && selectedId === item.id;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingThumb(true);
      try {
        const url = await fetchDownloadUrl(item.id);
        if (!cancelled) setThumbUrl(url);
      } catch {
        if (!cancelled) setThumbUrl(null);
      } finally {
        if (!cancelled) setLoadingThumb(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [item.id, fetchDownloadUrl]);

  return (
    <button
      type="button"
      className={`btn btn-link p-0 border rounded overflow-hidden text-start capture-media-tile ${
        isSelected ? 'border-primary border-2' : ''
      }`}
      style={{ width: 140 }}
      onClick={() => onSelect(item)}
      title={item.filename}
    >
      <div
        className="bg-light d-flex align-items-center justify-content-center"
        style={{ width: 140, height: 140 }}
      >
        {loadingThumb && (
          <span className="spinner-border spinner-border-sm text-muted" role="status" />
        )}
        {!loadingThumb && thumbUrl && (
          <img
            src={thumbUrl}
            alt={item.filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {!loadingThumb && !thumbUrl && (
          <i className="bi bi-image text-muted" style={{ fontSize: '2rem' }} aria-hidden />
        )}
      </div>
      <div className="p-2 small">
        <div className="fw-semibold text-truncate">{item.filename}</div>
        {item.usage_count > 0 && (
          <div className="text-muted text-truncate">Used in {item.usage_count} place(s)</div>
        )}
      </div>
    </button>
  );
}

MediaTile.propTypes = {
  item: PropTypes.object.isRequired,
  fetchDownloadUrl: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.number,
};

export default function MediaThumbnailGrid({
  items,
  loading,
  error,
  fetchDownloadUrl,
  onSelect,
  selectedId,
  emptyMessage,
}) {
  if (loading) {
    return (
      <div className="text-muted small">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Loading images…
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger py-2 small mb-0">{error}</div>;
  }

  if (!items.length) {
    return <div className="text-muted small">{emptyMessage || 'No images yet.'}</div>;
  }

  return (
    <div className="d-flex flex-wrap gap-3">
      {items.map((item) => (
        <MediaTile
          key={item.id}
          item={item}
          fetchDownloadUrl={fetchDownloadUrl}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </div>
  );
}

MediaThumbnailGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  fetchDownloadUrl: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.number,
  emptyMessage: PropTypes.string,
};

MediaThumbnailGrid.defaultProps = {
  loading: false,
  error: null,
  selectedId: null,
  emptyMessage: 'No images yet.',
};
